import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { baseErrorHandler } from '@queries/baseErrorHandler';
import { setHouseCurrency } from '@reducers/CurrencySlice';
import { setShowroomFlatCount } from '@reducers/ShowroomSlice';
import { $api } from '@service/RequestService';
import { HouseForShowroom } from '@utils/models/HouseForShowroom';
import { Pagination } from '@utils/models/Pagination';
import queryString from 'query-string';
import { useQuery } from 'react-query';

import { endPoints } from '../utils/constants/endPoints';
import { queryKeys } from '../utils/constants/queryKeys';
import { getShowroomData } from '../utils/helper/getShowroomData';
import { IShowroomBuilding } from '../utils/models/IShowroomBuilding';
import { IShowroomFilter } from '../utils/models/IShowroomFilter';
import { IShowroomFlat, ShowroomBookingType } from '../utils/models/IShowroomFlat';
import { IWebsiteBlockStatistic } from '../utils/models/IWebsiteBlockStatistic';

export function useSmartCatalogShowroom() {
  const dispatch = useAppDispatch();
  const { filter, isViewBookingBadge } = useAppSelector(state => state.showroomSlice);

  return useQuery<ReturnType<typeof getShowroomData>>(
    [queryKeys.SMART_CATALOG_SHOWROOM, filter.houseId, filter.buildingIds],
    async () => {
      const res = await $api.post(
        queryString.stringifyUrl({
          url: endPoints.SMART_CATALOG_SHOWROOM,
          query: {
            realtorBookingCount: isViewBookingBadge
          }
        }),
        {
          houseId: filter.houseId,
          buildingIds: filter.buildingIds
        }
      );

      const { data } = res.data;

      return getShowroomData(data);
    },
    {
      enabled: !!filter.houseId,
      onSuccess(data) {
        dispatch(setHouseCurrency(data?.currency));
      },
      onError: baseErrorHandler,
      refetchOnWindowFocus: false
    }
  );
}

export function useSmartCatalogShowroomFilter() {
  const dispatch = useAppDispatch();
  const { filter } = useAppSelector(state => state.showroomSlice);

  return useQuery<number[]>(
    [queryKeys.SMART_CATALOG_SHOWROOM_FILTER, { ...filter }],
    async () => {
      const res = await $api.post(endPoints.SMART_CATALOG_SHOWROOM, {
        ...filter,
        houseId: filter.houseId,
        buildingIds: filter.buildingIds
      });

      const { data } = res.data;

      return data?.map((item: { id: string | number }) => item?.id);
    },
    {
      onSuccess(data) {
        dispatch(setShowroomFlatCount(data?.length));
      },
      enabled: !!filter.houseId,
      onError: baseErrorHandler,
      refetchOnWindowFocus: false
    }
  );
}

export function useSmartCatalogHouse() {
  return useQuery<HouseForShowroom[]>(
    [queryKeys.SMART_CATALOG_HOUSE],
    async () => {
      const res = await $api.get(endPoints.SMART_CATALOG_HOUSE);

      return res?.data?.data;
    },
    {
      onError: baseErrorHandler,
      refetchOnWindowFocus: false
    }
  );
}

export function useSmartCatalogBuilding(houseId?: number) {
  return useQuery<IShowroomBuilding[]>(
    [queryKeys.SMART_CATALOG_BUILDING_ID, houseId],
    async () => {
      const res = await $api.get(endPoints.SMART_CATALOG_BUILDING_ID(houseId || 0));

      return res?.data?.data;
    },
    {
      enabled: !!houseId,
      onError: baseErrorHandler,
      refetchOnWindowFocus: false
    }
  );
}

export function useSmartCatalogFlat(id: number | null) {
  return useQuery<IShowroomFlat>(
    [queryKeys.SMART_CATALOG_FLAT_ID, id],
    async () => {
      const res = await $api.get(endPoints.SMART_CATALOG_FLAT_ID(id || 0));

      return res?.data?.data;
    },
    {
      enabled: !!id,
      onError: baseErrorHandler,
      refetchOnWindowFocus: false
    }
  );
}

export function useSmartCatalogTable() {
  const dispatch = useAppDispatch();
  const filter = useAppSelector(state => state.showroomSlice?.filter);

  return useQuery<Pagination<IShowroomFlat>>(
    [queryKeys.SMART_CATALOG_TABLE, filter],
    async () => {
      const res = await $api.post(endPoints.SMART_CATALOG_TABLE, filter);

      return res?.data?.data;
    },
    {
      enabled: !!filter?.houseId,
      onSuccess(data) {
        dispatch(setShowroomFlatCount(data?.totalElements));
      },
      onError: baseErrorHandler,
      refetchOnWindowFocus: false
    }
  );
}

export function useSmartCatalogFilterProperties() {
  const { houseId } = useAppSelector(state => state.showroomSlice.filter);

  return useQuery<IShowroomFilter>(
    [queryKeys.SMART_CATALOG_FILTER_PROPERTIES, houseId],
    async () => {
      const res = await $api.get(endPoints.SMART_CATALOG_FILTER_PROPERTIES(houseId![0] || 0));

      return res?.data?.data;
    },
    {
      enabled: !!houseId,
      onError: baseErrorHandler,
      refetchOnWindowFocus: false
    }
  );
}

export function useWebsiteBlockStatistic() {
  const { filter } = useAppSelector(state => state.showroomSlice);

  return useQuery<IWebsiteBlockStatistic>(
    [queryKeys.WEBSITE_BLOCK_STATISTIC, filter?.houseId],
    async () => {
      const res = await $api.get(`/v2/website/block-statistic/${filter?.houseId}`);

      return res?.data?.data;
    },
    {
      enabled: !!filter?.houseId,
      onError: baseErrorHandler,
      refetchOnWindowFocus: false
    }
  );
}

export function useShowroomBooking() {
  const { drawer } = useAppSelector(state => state.showroomSlice);
  const isViewBookingBadge = useAppSelector(
    ({ userReducer, showroomSlice }) =>
      userReducer.isPermissions.PERMISSION_REALTOR_BOOKING && showroomSlice.isViewBookingBadge
  );

  const { data, ...arg } = useQuery<{ bookings: ShowroomBookingType[] }>(
    ['showroom-booking', drawer?.id],
    async () => {
      const { data } = await $api.get(`v1/realtor-booking/all/${drawer?.id}?size=500`);

      return { bookings: data?.data?.data };
    },
    {
      enabled: !!drawer?.id && drawer?.visible && isViewBookingBadge
    }
  );

  return { ...data, ...arg };
}
